import React, { useEffect, useMemo, useState } from 'react';
import { Form, Switch, Cascader, Radio } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { T, H_TITLE, H_SUB, SECTION_LABEL } from './declarationTheme';
import { Field, Grid2, Grid3, Input, DatePicker, TimePicker, Select, inputProps, selectProps } from './fields';
import SelectWithAdd from '../../../components/SelectWithAdd';
import { TYPES_DOMMAGE } from '../../../variables/constants';
import togoLocalites from '../../../variables/togo/localites.json';
import { regleAgglomeration } from './regleAgglomeration';

const { TextArea } = Input;
const { Option } = Select;

/* ─── Options Cascader Togo : Région › Préfecture › Commune ─── */
const TOGO_CASCADER_OPTIONS = togoLocalites.map(r => ({
  value: r.nom,
  label: r.nom,
  children: r.prefectures.map(p => ({
    value: p.nom,
    label: p.chefLieu && p.chefLieu !== p.nom ? `${p.nom} (${p.chefLieu})` : p.nom,
    chefLieu: p.chefLieu,
    children: p.communes.map(c => ({
      value: c.nom,
      label: c.nom,
    })),
  })),
}));

/* Index inversé : commune (string) → { region, prefecture, chefLieu } pour pré-remplissage à l'édition */
const TOGO_COMMUNE_INDEX = (() => {
  const idx = {};
  togoLocalites.forEach(r => r.prefectures.forEach(p => p.communes.forEach(c => {
    idx[c.nom.toLowerCase()] = { region: r.nom, prefecture: p.nom, chefLieu: p.chefLieu };
  })));
  return idx;
})();

/* Filtrage : tape "Lomé" → trouve toutes les Golfe (chef-lieu match), tape "Kara" → Kozah, etc. */
const filterTogoCascader = (inputValue, path) => {
  const q = inputValue.toLowerCase();
  return path.some(opt =>
    opt.label.toLowerCase().includes(q) ||
    (opt.chefLieu && opt.chefLieu.toLowerCase().includes(q))
  );
};

/* 
   Étape 5 - Détails de l'accident
   Le pays est déjà choisi à l'étape 1 (Contexte).
   Ville et commune paramétrées via /v1/parametres/categorie/VILLE
   et COMMUNE - liste plate tous pays confondus, extensible à la volée.
    */
export default function StepAccident({ form, pays = [] }) {
  const { t } = useTranslation('declaration');
  const paysGestionnaireTrackingId = Form.useWatch('paysGestionnaireTrackingId', form);
  const communeWatch = Form.useWatch('commune', form);
  const togoLocaliteWatch = Form.useWatch('togoLocalite', form);
  const pvEtabliWatch = Form.useWatch('pvEtabli', form);
  const lieuAccidentWatch = Form.useWatch('lieuAccident', form);
  const agglomeration = regleAgglomeration({
    pvEtabli: pvEtabliWatch, lieuAccident: lieuAccidentWatch,
  });

  /* Retirer le PV efface la reponse. Sans cela, un choix fait puis retire partirait
     quand meme au serveur, et le dossier porterait une affirmation que le
     formulaire ne montre plus. */
  useEffect(() => {
    if (!agglomeration.visible) form.setFieldsValue({ agglomeration: undefined });
  }, [agglomeration.visible, form]);

  /* Pays de l'accident = pays gestionnaire (affiché à titre indicatif) */
  const [paysAccident, setPaysAccident] = useState(null);
  useEffect(() => {
    setPaysAccident(pays.find(p => p.paysTrackingId === paysGestionnaireTrackingId) || null);
  }, [paysGestionnaireTrackingId, pays]);

  const isTogo = useMemo(() => {
    if (!paysAccident) return false;
    const iso = String(paysAccident.codeIso || '').trim().toUpperCase();
    const bcb = String(paysAccident.codeCarteBrune || '').trim().toUpperCase();
    const lib = String(paysAccident.libelle || '').trim().toLowerCase();
    return iso === 'TGO' || iso === 'TG' || bcb === 'TG' || bcb === 'TGO' || lib === 'togo';
  }, [paysAccident]);

  /* Reconstitution du Cascader si la commune existante correspond à une commune Togo connue
     (utile en mode édition ou après restauration brouillon) */
  useEffect(() => {
    if (!isTogo) return;
    if (togoLocaliteWatch && togoLocaliteWatch.length === 3) return;
    if (!communeWatch) return;
    const hit = TOGO_COMMUNE_INDEX[String(communeWatch).toLowerCase()];
    if (hit) {
      form.setFieldValue('togoLocalite', [hit.region, hit.prefecture, communeWatch]);
    }
  }, [isTogo, communeWatch, togoLocaliteWatch, form]);

  /* Au changement du Cascader → écrit ville (chef-lieu) + commune */
  const onTogoLocaliteChange = (value, selectedOptions) => {
    if (!value || value.length === 0) {
      form.setFieldsValue({ togoLocalite: undefined });
      return;
    }
    const [, , communeNom] = value;
    const prefOpt = selectedOptions?.[1];
    form.setFieldsValue({
      togoLocalite: value,
      ville: prefOpt?.chefLieu || prefOpt?.value || undefined,
      commune: communeNom || undefined,
    });
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <h1 style={H_TITLE}>{t('accident.titre')}</h1>
      <p style={H_SUB}>{t('accident.paysAccident', { pays: paysAccident?.libelle || '-' })}</p>

      {/*  Date & heure  */}
      <div style={{ marginTop: 40 }}>
        <div style={SECTION_LABEL}>{t('accident.dateHeure')}</div>

        <Grid3>
          <Field
            name="dateAccident"
            label={t('accident.dateAccident')}
            rules={[{ required: true, message: t('accident.dateAccidentRequise') }]}
          >
            <DatePicker
              style={{ ...inputProps.style, width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={t('commun.formatDate')}
              disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
            />
          </Field>

          <Field name="heureAccident" label={t('accident.heureAccident')}>
            <TimePicker
              style={{ ...inputProps.style, width: '100%' }}
              format="HH:mm"
              placeholder="hh:mm"
            />
          </Field>

          <Field
            name="dateDeclaration"
            label={t('accident.dateDeclaration')}
            hint={t('accident.dateDeclarationIndication')}
            rules={[{ required: true, message: t('commun.obligatoire') }]}
          >
            <DatePicker
              style={{ ...inputProps.style, width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={t('commun.formatDate')}
              disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
            />
          </Field>
        </Grid3>
      </div>

      {/*  Lieu  */}
      <div style={{ marginTop: 20 }}>
        <div style={SECTION_LABEL}>{t('accident.lieu')}</div>

        {isTogo ? (
          <>
            <Field
              name="togoLocalite"
              label={t('accident.localisationTogo')}
            >
              <Cascader
                options={TOGO_CASCADER_OPTIONS}
                onChange={onTogoLocaliteChange}
                placeholder={t('accident.localisationTogoExemple')}
                showSearch={{ filter: filterTogoCascader, matchInputWidth: false }}
                expandTrigger="hover"
                changeOnSelect={false}
                allowClear
                style={{ width: '100%' }}
                size="large"
              />
            </Field>
            {/* Champs cachés alimentés par le Cascader - gardent la compat backend (ville/commune) */}
            <Form.Item name="ville" hidden><Input /></Form.Item>
            <Form.Item name="commune" hidden><Input /></Form.Item>
          </>
        ) : (
          <Grid2>
            <Field
              name="ville"
              label={t('accident.ville')}
            >
              <SelectWithAdd
                categorie="VILLE"
                valueKey="valeur"
                placeholder={paysAccident ? t('accident.selectionnerVillePays', { pays: paysAccident.libelle }) : t('accident.selectionnerVille')}
                addLabel={t('accident.ajouterVille')}
                style={{ width: '100%' }}
                size="large"
              />
            </Field>

            <Field name="commune" label={t('accident.commune')}>
              <SelectWithAdd
                categorie="COMMUNE"
                valueKey="valeur"
                placeholder={t('accident.selectionnerCommune')}
                addLabel={t('accident.ajouterCommune')}
                style={{ width: '100%' }}
                size="large"
              />
            </Field>
          </Grid2>
        )}

        <Field
          name="lieuAccident"
          label={t('accident.lieuPrecis')}
        >
          <TextArea
            rows={2}
            placeholder={t('accident.lieuPrecisExemple')}
            maxLength={500}
            showCount
            style={{ borderRadius: T.r.md, borderColor: T.line }}
          />
        </Field>

      </div>

      {/*  Trajet & type dommage  */}
      <div style={{ marginTop: 12 }}>
        <div style={SECTION_LABEL}>{t('accident.trajet')}</div>

        <Grid2>
          <Field name="provenance" label={t('accident.provenance')}>
            <Input {...inputProps} placeholder={t('accident.provenanceExemple')} maxLength={150} />
          </Field>
          <Field name="destination" label={t('accident.destination')}>
            <Input {...inputProps} placeholder={t('accident.destinationExemple')} maxLength={150} />
          </Field>
        </Grid2>
      </div>

      {/*  Constat & dommage  */}
      <div style={{ marginTop: 12 }}>
        <div style={SECTION_LABEL}>{t('accident.constatDommages')}</div>

        <Field
          name="typeDommage"
          label={t('accident.natureDommage')}
          rules={[{ required: true, message: t('commun.obligatoire') }]}
        >
          <Select {...selectProps} placeholder={t('commun.selectionner')}>
            {TYPES_DOMMAGE.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
          </Select>
        </Field>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            border: `1px solid ${T.line}`,
            borderRadius: T.r.md,
            marginTop: 4,
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.inkSoft }}>
              {t('accident.rapportPolice')}
            </div>
            <div style={{ fontSize: 12, color: T.inkMuted, marginTop: 2 }}>
              {t('accident.rapportPoliceAide')}
            </div>
          </div>
          <Form.Item name="pvEtabli" valuePropName="checked" noStyle>
            <Switch checkedChildren={t('commun.oui')} unCheckedChildren={t('commun.non')} />
          </Form.Item>
        </div>

        {/* La question ne se pose que si un PV est fourni : c'est lui qui porte
            l'information. Un interrupteur ne conviendrait pas - il est toujours sur
            une position, donc le rendre « obligatoire » ne voudrait rien dire. */}
        {agglomeration.visible && (
          <Field
            name="agglomeration"
            label={t('accident.agglomeration')}
            rules={agglomeration.obligatoire
              ? [{ required: true, message: t('accident.agglomerationRequise') }]
              : []}
          >
            <Radio.Group optionType="button" buttonStyle="solid">
              <Radio.Button value>{t('commun.oui')}</Radio.Button>
              <Radio.Button value={false}>{t('commun.non')}</Radio.Button>
            </Radio.Group>
          </Field>
        )}
      </div>

      {/*  Circonstances  */}
      <div style={{ marginTop: 20 }}>
        <div style={SECTION_LABEL}>{t('accident.circonstances')}</div>

        <Field
          name="circonstances"
          label={t('accident.description')}
        >
          <TextArea
            rows={5}
            placeholder={t('accident.descriptionExemple')}
            maxLength={2000}
            showCount
            style={{ borderRadius: T.r.md, borderColor: T.line }}
          />
        </Field>
      </div>
    </div>
  );
}
